import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Star, Lock, AlertTriangle } from "lucide-react";

const inputCls = "bg-white border-steel text-charcoal h-11";
const STATUS_STYLES = {
  draft: "bg-elevated border-steel text-slate-500",
  submitted: "bg-amber-100 border-amber-300 text-amber-800",
  verified: "bg-emerald-100 border-emerald-300 text-emerald-800",
  returned: "bg-red-100 border-red-300 text-red-700",
};
const STATUS_LABELS = { draft: "Draft", submitted: "Submitted — awaiting review", verified: "Verified by Lomond", returned: "Returned for amendment" };

const BLANK = {
  week_ending: "", date_from: "", date_to: "", customer: "", site: "", location: "", supervisor: "",
  machine_category: "", machine_size: "", fleet_number: "", attachment: "",
  total_hours: "", seat_hours: "", non_operating_hours: "", tasks: [], tasks_other: "",
  reflection: { completed: "", confident: "", difficult: "", safety: "" },
};

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}

export function LiveExperience({ data, reload }) {
  const exp = data.experience || { records: [], summary: {}, logging_open: true };
  const summary = exp.summary || {};
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState("");
  const [dupWarning, setDupWarning] = useState("");
  const [expanded, setExpanded] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setForm((f) => ({ ...f, reflection: { ...f.reflection, [k]: v } }));

  const startNew = (weekEnding) => {
    setEditingId(null);
    setForm({ ...BLANK, week_ending: weekEnding || exp.expected_week_ending || "" });
    setDupWarning("");
    setOpen(true);
  };

  const startAmend = (r) => {
    setEditingId(r.id);
    setForm({
      week_ending: r.week_ending || "", date_from: r.date_from || "", date_to: r.date_to || "",
      customer: r.customer || "", site: r.site || "", location: r.location || "", supervisor: r.supervisor || "",
      machine_category: r.machine_category || "", machine_size: r.machine_size || "",
      fleet_number: r.fleet_number || "", attachment: r.attachment || "",
      total_hours: r.total_hours ?? "", seat_hours: r.seat_hours ?? "", non_operating_hours: r.non_operating_hours ?? "",
      tasks: r.tasks || [], tasks_other: r.tasks_other || "",
      reflection: { completed: "", confident: "", difficult: "", safety: "", ...(r.reflection || {}) },
    });
    setDupWarning("");
    setOpen(true);
  };

  const toggleTask = (t) => setForm((f) => ({
    ...f, tasks: f.tasks.includes(t) ? f.tasks.filter((x) => x !== t) : [...f.tasks, t],
  }));

  const payload = (submit, allowDuplicate) => ({
    ...form,
    total_hours: parseFloat(form.total_hours) || 0,
    seat_hours: parseFloat(form.seat_hours) || 0,
    non_operating_hours: parseFloat(form.non_operating_hours) || 0,
    submit, allow_duplicate: allowDuplicate,
  });

  const save = async (submit, allowDuplicate = false) => {
    setSaving(submit ? "submit" : "draft");
    setDupWarning("");
    try {
      if (editingId) {
        await api.put(`/development/experience/${editingId}`, payload(submit, allowDuplicate));
        if (submit) await api.post(`/development/experience/${editingId}/submit`);
      } else {
        await api.post("/development/experience", payload(submit, allowDuplicate));
      }
      toast.success(submit ? "Live Experience submitted for Lomond to verify." : "Draft saved.");
      setOpen(false);
      setEditingId(null);
      reload();
    } catch (e) {
      if (e?.response?.status === 409) {
        setDupWarning(apiError(e));
      } else {
        toast.error(apiError(e));
      }
    } finally { setSaving(""); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-charcoal text-white rounded-sm p-3.5 text-center col-span-2 sm:col-span-1">
          <p className="font-heading font-black text-2xl text-[#FFC800]" data-testid="exp-verified-total">{summary.verified_total ?? 0}</p>
          <p className="text-[9px] font-mono uppercase tracking-widest text-white/50 mt-1">Total verified seat hours</p>
        </div>
        <div className="bg-elevated rounded-sm p-3.5 text-center">
          <p className="font-heading font-black text-xl text-charcoal" data-testid="exp-this-week">+{summary.this_week ?? 0}h</p>
          <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">This week (verified)</p>
        </div>
        <div className="bg-elevated rounded-sm p-3.5 text-center">
          <p className="font-heading font-black text-xl text-charcoal" data-testid="exp-machines">{Object.keys(summary.by_machine || {}).length}</p>
          <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Machines used</p>
        </div>
        <div className="bg-elevated rounded-sm p-3.5 text-center">
          <p className="font-heading font-black text-xl text-charcoal" data-testid="exp-task-count">{Object.keys(summary.by_task || {}).length}</p>
          <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Experience areas</p>
        </div>
      </div>
      {Object.keys(summary.by_task || {}).length > 0 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="exp-task-summary">
          {Object.entries(summary.by_task).map(([t, h]) => (
            <span key={t} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-1">{t}: <strong>{h}h</strong></span>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-500">
        Seat hours are evidence of experience — not automatic competence. You record your week; Lomond verifies it and assesses competence separately.
      </p>

      {exp.due && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="experience-due-banner">
          <p className="text-sm text-amber-800 font-semibold flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            LIVE EXPERIENCE DUE — you have completed your timesheet for week ending {exp.expected_week_ending} but have not yet submitted your Live Experience.
          </p>
          <Button size="sm" onClick={() => startNew(exp.expected_week_ending)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="exp-complete-btn">
            Complete Live Experience
          </Button>
        </div>
      )}

      {!exp.logging_open ? (
        <p className="text-sm text-slate-600 bg-elevated border border-steel rounded-md p-4" data-testid="logging-closed-note">
          Development logging complete — your full history below is retained as your competence evidence.
        </p>
      ) : !open && (
        <Button onClick={() => startNew()} className="bg-lomond text-black font-bold hover:bg-[#E6B400] w-full sm:w-auto" data-testid="add-experience-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Live Experience
        </Button>
      )}

      {open && (
        <div className="bg-white border-2 border-lomond rounded-md p-4 sm:p-5 space-y-5" data-testid="exp-form">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">
            {editingId ? "Amend Live Experience" : "New Live Experience"} — takes about 2–4 minutes
          </p>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Dates (match your timesheet week)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <Field label="Week ending *" testid="exp-f-week-ending"><Input type="date" value={form.week_ending} onChange={(e) => set("week_ending", e.target.value)} className={inputCls} data-testid="exp-week-ending" /></Field>
              <Field label="From date *" testid="exp-f-date-from"><Input type="date" value={form.date_from} onChange={(e) => set("date_from", e.target.value)} className={inputCls} data-testid="exp-date-from" /></Field>
              <Field label="To date" testid="exp-f-date-to"><Input type="date" value={form.date_to} onChange={(e) => set("date_to", e.target.value)} className={inputCls} data-testid="exp-date-to" /></Field>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Site / Customer</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <Field label="Customer *" testid="exp-f-customer"><Input value={form.customer} onChange={(e) => set("customer", e.target.value)} className={inputCls} data-testid="exp-customer" /></Field>
              <Field label="Site / project" testid="exp-f-site"><Input value={form.site} onChange={(e) => set("site", e.target.value)} className={inputCls} data-testid="exp-site" /></Field>
              <Field label="Location" testid="exp-f-location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} data-testid="exp-location" /></Field>
              <Field label="Supervisor / site contact (if known)" testid="exp-f-supervisor"><Input value={form.supervisor} onChange={(e) => set("supervisor", e.target.value)} className={inputCls} data-testid="exp-supervisor" /></Field>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Machine</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <Field label="Machine category" testid="exp-f-machine-category">
                <select value={form.machine_category} onChange={(e) => set("machine_category", e.target.value)} className="h-11 w-full rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="exp-machine-category">
                  <option value="">Select…</option>
                  {(data.machine_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Machine size" testid="exp-f-machine-size">
                <select value={form.machine_size} onChange={(e) => set("machine_size", e.target.value)} className="h-11 w-full rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid="exp-machine-size">
                  <option value="">Select…</option>
                  {(data.machine_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Fleet number / registration (if known)" testid="exp-f-fleet"><Input value={form.fleet_number} onChange={(e) => set("fleet_number", e.target.value)} className={inputCls} data-testid="exp-fleet" /></Field>
              <Field label="Attachment used" testid="exp-f-attachment"><Input value={form.attachment} onChange={(e) => set("attachment", e.target.value)} className={inputCls} data-testid="exp-attachment" /></Field>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Hours — seat hours matter most</p>
            <div className="grid grid-cols-3 gap-2.5">
              <Field label="Total hours" testid="exp-f-total"><Input type="number" min="0" step="0.5" value={form.total_hours} onChange={(e) => set("total_hours", e.target.value)} className={inputCls} data-testid="exp-total-hours" /></Field>
              <Field label="Seat hours *" testid="exp-f-seat"><Input type="number" min="0" step="0.5" value={form.seat_hours} onChange={(e) => set("seat_hours", e.target.value)} className={`${inputCls} border-lomond`} data-testid="exp-seat-hours" /></Field>
              <Field label="Travel / non-operating" testid="exp-f-nonop"><Input type="number" min="0" step="0.5" value={form.non_operating_hours} onChange={(e) => set("non_operating_hours", e.target.value)} className={inputCls} data-testid="exp-nonop-hours" /></Field>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Tasks completed (tap all that apply)</p>
            <div className="flex flex-wrap gap-2" data-testid="exp-tasks">
              {(data.task_tags || []).map((t) => (
                <button key={t} type="button" onClick={() => toggleTask(t)}
                  className={`px-3 py-2.5 rounded-sm border text-xs transition-colors ${
                    form.tasks.includes(t) ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-600 hover:border-lomond"
                  }`}
                  data-testid={`exp-task-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {t}
                </button>
              ))}
            </div>
            {form.tasks.includes("Other") && (
              <Input value={form.tasks_other} onChange={(e) => set("tasks_other", e.target.value)} placeholder="Describe the other work" className={`${inputCls} mt-2`} data-testid="exp-tasks-other" />
            )}
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Quick reflection (short answers are fine)</p>
            <div className="space-y-2.5">
              {[["completed", "What work did you complete this week?"], ["confident", "What do you feel more confident doing now?"],
                ["difficult", "Anything you found difficult or need more help with?"], ["safety", "Any safety issues, near misses or unusual site conditions?"]].map(([k, l]) => (
                <Field key={k} label={l} testid={`exp-f-reflect-${k}`}>
                  <Textarea value={form.reflection[k]} onChange={(e) => setR(k, e.target.value)} className="bg-white border-steel text-charcoal min-h-16" data-testid={`exp-reflect-${k}`} />
                </Field>
              ))}
            </div>
          </div>

          {dupWarning && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="exp-dup-warning">
              <p className="text-sm text-amber-800 font-semibold">{dupWarning}</p>
              <Button size="sm" variant="outline" onClick={() => save(true, true)} disabled={!!saving}
                className="mt-2 border-amber-400 text-amber-800 bg-white" data-testid="exp-dup-confirm-btn">
                Submit anyway for this period
              </Button>
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-1">
            <Button onClick={() => save(true)} disabled={!!saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="exp-submit-btn">
              {saving === "submit" ? "Submitting…" : "Submit for Verification"}
            </Button>
            <Button onClick={() => save(false)} disabled={!!saving} variant="outline" className="border-steel text-charcoal bg-white" data-testid="exp-save-draft-btn">
              {saving === "draft" ? "Saving…" : "Save Draft"}
            </Button>
            <Button onClick={() => { setOpen(false); setEditingId(null); }} variant="outline" className="border-steel text-slate-500 bg-white" data-testid="exp-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-2.5" data-testid="experience-records">
        {exp.records.map((r) => (
          <div key={r.id} className="bg-elevated rounded-md p-3.5" data-testid={`exp-record-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <button type="button" className="text-left min-w-0" onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`exp-expand-${r.id}`}>
                <p className="text-sm font-semibold text-charcoal">Week ending {r.week_ending} — {r.customer}{r.site ? ` — ${r.site}` : ""}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {r.date_from}{r.date_to ? ` → ${r.date_to}` : ""}
                  {r.machine_category ? ` — ${[r.machine_category, r.machine_size].filter(Boolean).join(" ")}` : ""}
                </p>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono font-bold text-charcoal text-sm" data-testid={`exp-hours-${r.id}`}>{r.seat_hours}h seat</span>
                <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLES[r.status]}`} data-testid={`exp-status-${r.id}`}>{STATUS_LABELS[r.status]}</span>
              </div>
            </div>
            {r.status === "returned" && (
              <div className="mt-2 bg-red-50 border border-red-200 rounded-sm p-2.5" data-testid={`exp-returned-${r.id}`}>
                <p className="text-xs text-red-700"><strong>Returned by Lomond:</strong> {r.return_reason}</p>
                <Button size="sm" onClick={() => startAmend(r)} className="mt-2 bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid={`exp-amend-${r.id}`}>Amend & Resubmit</Button>
              </div>
            )}
            {r.status === "verified" && (
              <p className="text-[10px] text-slate-500 font-mono mt-1.5 flex items-center gap-1" data-testid={`exp-locked-${r.id}`}>
                <Lock className="h-3 w-3" /> Verified{r.verified_by_name ? ` by ${r.verified_by_name}` : ""} — contact Lomond if a correction is needed.
              </p>
            )}
            {expanded === r.id && (
              <div className="mt-3 space-y-2 border-t border-steel pt-3" data-testid={`exp-detail-${r.id}`}>
                {r.tasks?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {r.tasks.map((t) => <span key={t} className="text-[10px] font-mono bg-white border border-steel rounded-sm px-2 py-0.5">{t}</span>)}
                    {r.tasks_other && <span className="text-[10px] font-mono bg-white border border-steel rounded-sm px-2 py-0.5">Other: {r.tasks_other}</span>}
                  </div>
                )}
                {Object.entries(r.reflection || {}).filter(([, v]) => v).map(([k, v]) => (
                  <p key={k} className="text-xs text-slate-600"><span className="font-semibold text-charcoal capitalize">{k}:</span> {v}</p>
                ))}
                {r.review && (
                  <div className="bg-white border border-steel rounded-sm p-3" data-testid={`exp-review-${r.id}`}>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-lomond mb-1.5 flex items-center gap-1"><Star className="h-3 w-3" /> Supervisor review — {r.review.by_name}</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {Object.entries(r.review.ratings || {}).map(([f, v]) => (
                        <span key={f} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-2 py-0.5">{f.replace(/_/g, " ")} <strong>{v}/5</strong></span>
                      ))}
                    </div>
                    {r.review.comments && <p className="text-xs text-slate-600 mt-1.5">"{r.review.comments}"</p>}
                    {r.review.development_points && <p className="text-xs text-amber-800 mt-1"><strong>Development points:</strong> {r.review.development_points}</p>}
                    {r.review.strengths && <p className="text-xs text-emerald-800 mt-1"><strong>Strengths:</strong> {r.review.strengths}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!exp.records.length && (
          <p className="text-sm text-slate-500" data-testid="exp-empty">
            {exp.logging_open ? "No Live Experience submitted yet — add your first week above." : "No Live Experience records on file."}
          </p>
        )}
      </div>

      {data.assignments.length > 0 && (
        <div className="pt-2 border-t border-steel">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Assignments recorded by Lomond</p>
          <div className="space-y-2.5" data-testid="assignment-list">
            {data.assignments.map((a) => (
              <div key={a.id} className="bg-elevated rounded-md p-3.5" data-testid={`assignment-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-charcoal">{a.customer}{a.site ? ` — ${a.site}` : ""}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{a.start_date}{a.end_date ? ` → ${a.end_date}` : ""} — {a.work_type}{a.machine ? ` — ${a.machine}` : ""}</p>
                  </div>
                  <span className="font-mono font-bold text-charcoal text-sm">{a.seat_hours}h</span>
                </div>
                {a.feedback && (
                  <p className="text-xs text-slate-600 mt-2 border-l-2 border-lomond pl-2.5 flex items-start gap-1.5">
                    <Star className="h-3 w-3 text-lomond shrink-0 mt-0.5" />
                    {a.feedback.comments || "Feedback recorded"} — {a.feedback.by_name}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
